// Muscle-map verifier. Reachable at /manage/diagram (not in the main
// nav). Pick a muscle group, see exactly which paths in the front + back
// SVGs are mapped to it. The mapping comes from scripts/build-muscle-map.ts
// run against the Figma export — anything wrong here is a hint to either
// fix FIGMA_TO_GROUP in the script, or to make our taxonomy more granular.

import { useEffect, useMemo, useState } from 'react';
import { ManageLayout } from '../../components/ManageLayout';
import { MuscleMapInspector } from '../../components/MuscleMapInspector';
import muscleMapJson from '../../assets/diagram/muscle-map.json';
import css from './Diagram.module.css';

interface MuscleGroup {
  id: string;
  name: string;
}

type View = 'front' | 'back';

const MUSCLE_INDICES = muscleMapJson.muscleIndices as Record<View, Record<string, number[]>>;

export function ManageDiagram() {
  const [muscles, setMuscles] = useState<MuscleGroup[] | null>(null);
  const [activeMuscle, setActiveMuscle] = useState<string | null>(null);
  const [showAllMapped, setShowAllMapped] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/manage/muscles')
      .then((r) => r.json())
      .then((d) => setMuscles(d.groups))
      .catch(() => setError("couldn't load muscle groups"));
  }, []);

  function counts(muscleId: string): { front: number; back: number } {
    return {
      front: (MUSCLE_INDICES.front[muscleId] ?? []).length,
      back: (MUSCLE_INDICES.back[muscleId] ?? []).length,
    };
  }

  const allMappedFront = useMemo(() => {
    const s = new Set<number>();
    for (const ids of Object.values(MUSCLE_INDICES.front)) for (const i of ids) s.add(i);
    return s;
  }, []);
  const allMappedBack = useMemo(() => {
    const s = new Set<number>();
    for (const ids of Object.values(MUSCLE_INDICES.back)) for (const i of ids) s.add(i);
    return s;
  }, []);

  return (
    <ManageLayout title="Diagram">
      {error && <div className={css.error}>{error}</div>}

      <div className={css.layout}>
        <aside className={css.sidebar}>
          <div className={css.section}>
            <h3 className={css.sectionTitle}>Muscle groups</h3>
            <p className={css.hint}>
              Click a group → see which paths are highlighted on the figure.
              Counts show how many path indices are mapped per view.
            </p>
            <ul className={css.muscleList}>
              {muscles?.map((m) => {
                const c = counts(m.id);
                const isActive = activeMuscle === m.id;
                return (
                  <li key={m.id}>
                    <button
                      type="button"
                      className={isActive ? `${css.muscleRow} ${css.muscleRowActive}` : css.muscleRow}
                      onClick={() => {
                        setActiveMuscle(isActive ? null : m.id);
                        setShowAllMapped(false);
                      }}
                    >
                      <span>{m.name}</span>
                      <span className={css.counts}>
                        {c.front}f / {c.back}b
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
          <div className={css.section}>
            <h3 className={css.sectionTitle}>Debug</h3>
            <button
              type="button"
              className={showAllMapped ? `${css.muscleRow} ${css.muscleRowActive}` : css.muscleRow}
              onClick={() => {
                setShowAllMapped(!showAllMapped);
                setActiveMuscle(null);
              }}
            >
              <span>highlight all mapped paths</span>
              <span className={css.counts}>
                {allMappedFront.size}f / {allMappedBack.size}b
              </span>
            </button>
            <p className={css.hint} style={{ marginTop: 8 }}>
              Green: any path that's mapped to at least one muscle group.
              Unhighlighted paths are still gray = nothing tagged them. If
              an obvious muscle is gray, the mapping needs work.
            </p>
          </div>
        </aside>

        <MuscleMapInspector
          activeMuscle={activeMuscle}
          showAllMapped={showAllMapped}
          muscles={muscles ?? []}
        />
      </div>
    </ManageLayout>
  );
}
